import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { NAV, CART } from "@/constants/testIds";
import { trackEvent } from "@/lib/analytics";
import { useCart } from "@/context/CartContext";

const links = [
  { id: "shop", label: "Shop", href: "/shop" },
  { id: "academy", label: "Academy", href: "/academy" },
  { id: "ourstory", label: "Our Story", href: "/#our-story" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 12);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const goToShop = (e) => {
    e?.preventDefault();
    setOpen(false);
    trackEvent("cta_click", { button_text: "Buy Now", button_location: "navbar" });
    navigate("/shop");
  };

  const trackNavClick = (label, location_) => {
    trackEvent("navigation_click", { button_text: label, button_location: location_ });
  };

  return (
    <header
      ref={ref}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-24 md:h-32 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center group shrink-0"
        >
          <img
            src="/pickle-wall-logo.png"
            alt="Pickle Wall — Hold The Line"
            className="h-20 md:h-28 w-auto object-contain group-hover:scale-105 transition-transform"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          <Link
            to="/shop"
            data-testid={NAV.link("shop")}
            onClick={() => trackNavClick("Shop", "navbar-desktop")}
            className="text-sm font-medium text-white/70 hover:text-white transition-colors uppercase tracking-wider whitespace-nowrap"
          >
            Shop
          </Link>
          <Link
            to="/academy"
            data-testid={NAV.link("academy")}
            onClick={() => trackNavClick("Academy", "navbar-desktop")}
            className="text-sm font-bold text-[#FF6B00] hover:text-[#ff8a3d] transition-colors uppercase tracking-wider whitespace-nowrap"
          >
            Academy
          </Link>
          <a
            href="/#our-story"
            data-testid={NAV.link("ourstory")}
            onClick={() => trackNavClick("Our Story", "navbar-desktop")}
            className="text-sm font-medium text-white/70 hover:text-white transition-colors uppercase tracking-wider whitespace-nowrap mr-2"
          >
            Our Story
          </a>
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            data-testid={CART.trigger}
            onClick={() => {
              trackEvent("cart_opened", { button_location: "navbar" });
              setCartOpen(true);
            }}
            aria-label="Open cart"
            className="relative p-2 text-white hover:text-[#FF6B00] transition-colors"
          >
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#FF6B00] text-black text-[10px] font-bold flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button
            onClick={goToShop}
            data-testid={NAV.ctaPreorder}
            className="hidden sm:inline-flex items-center bg-[#FF6B00] hover:bg-[#E66000] text-black font-bold uppercase tracking-wider text-sm px-5 py-3 transition-all hover:orange-glow whitespace-nowrap"
          >
            Buy Now
          </button>
          <button
            data-testid={NAV.mobileToggle}
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div id="mobile-menu" className="lg:hidden glass-strong border-t border-white/5">
          <div className="px-6 py-6 flex flex-col gap-4 max-h-[80vh] overflow-y-auto">
            {links.map((l) =>
              l.href.startsWith("/") && !l.href.startsWith("/#") ? (
                <Link
                  key={l.id}
                  to={l.href}
                  data-testid={`${NAV.link(l.id)}-mobile`}
                  onClick={() => trackNavClick(l.label, "navbar-mobile")}
                  className="text-base font-medium text-white/80 uppercase tracking-wider"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.id}
                  href={l.href}
                  data-testid={`${NAV.link(l.id)}-mobile`}
                  onClick={() => trackNavClick(l.label, "navbar-mobile")}
                  className="text-base font-medium text-white/80 uppercase tracking-wider"
                >
                  {l.label}
                </a>
              )
            )}
            <button
              onClick={goToShop}
              data-testid={`${NAV.ctaPreorder}-mobile`}
              className="mt-2 bg-[#FF6B00] text-black font-bold uppercase tracking-wider text-sm px-5 py-3"
            >
              Buy Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
